import React from 'react'
import { Loading } from '@workwhile/ui'
import {
  Building2,
  Calendar,
  DollarSign,
  Hash,
  Receipt,
  User,
} from 'lucide-react'
import {
  CommandPaletteOption,
  CopyableValue,
  DetailsDescription,
  DetailsTitle,
  formatDateTime,
  getValue,
  PaymentEntry,
  SectionTitleWithButton,
} from '../CommandPalette_Utils'
import { useUserPayments } from '../../../queries/payments/useUserPayments'
import { InfoTable } from '../../../components/InfoTable/InfoTable'

type PaymentDetailsProps = {
  payment: PaymentEntry
  onNavigateToPayment: (paymentId: string | number) => void
  onItemInteraction: (item: CommandPaletteOption) => void
}

export function PaymentDetails({
  payment,
  onNavigateToPayment,
  onItemInteraction,
}: PaymentDetailsProps) {
  const { data: paymentDetails, isLoading: isLoadingPaymentDetails } =
    useUserPayments(
      { paymentId: Number(payment.id) },
      { staleTime: 1000 * 60 * 5 } // 5 minutes
    )
  const detailedPayment = paymentDetails?.items?.find(
    (item) => item.id === payment.id
  )

  const handleSearch = () => {
    const paymentId = payment.id
    if (
      paymentId &&
      (typeof paymentId === 'string' || typeof paymentId === 'number')
    ) {
      onItemInteraction(payment)
      onNavigateToPayment(paymentId)
    }
  }

  return (
    <>
      <DetailsTitle>
        Payment #{getValue(payment.id)}
        {isLoadingPaymentDetails ? (
          <span style={{ marginLeft: '8px' }}>
            <Loading type="button" />
          </span>
        ) : null}
      </DetailsTitle>
      <DetailsDescription>
        <div>
          <SectionTitleWithButton
            title="Payment Information"
            buttonText="View Payment"
            onButtonClick={handleSearch}
            buttonTitle="View payment details"
          />
          <InfoTable>
            <InfoTable.Row
              icon={<Hash size={14} />}
              label="Payment ID"
              value={
                <CopyableValue
                  value={payment.id}
                  fieldName="Payment ID"
                  item={payment}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<DollarSign size={14} />}
              label="Amount"
              value={`$${payment.amount ?? 'N/A'}`}
            />
            <InfoTable.Row
              icon={<Receipt size={14} />}
              label="Status"
              value={payment.status ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<Receipt size={14} />}
              label="Event Type"
              value={detailedPayment?.eventType ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<User size={14} />}
              label="User ID"
              value={detailedPayment?.userId ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Shift ID"
              value={detailedPayment?.shiftId ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<Building2 size={14} />}
              label="Company ID"
              value={detailedPayment?.companyId ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Created"
              value={formatDateTime(detailedPayment?.createdAt, 'UTC')}
            />
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Originated"
              value={formatDateTime(detailedPayment?.originatedAt, 'UTC')}
            />
          </InfoTable>
        </div>
      </DetailsDescription>
    </>
  )
}
